import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wallet,
  Loader2,
  ArrowLeft,
  Check,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { useOrgSettings } from "@/lib/org-settings-context";
import { QRCodeSVG } from "qrcode.react";

interface WalletTopUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type Step = "amount" | "qr" | "processing" | "success" | "failed";

const QUICK_AMOUNTS = [
  { value: "100", label: "100", popular: true },
  { value: "200", label: "200", popular: false },
  { value: "500", label: "500", popular: false },
  { value: "1000", label: "1,000", popular: false },
];
const DEFAULT_AMOUNT = "100";

interface PayHubQRData {
  paymentId: string;
  transactionId: string;
  paymentUrl: string;
  amount: number;
  expiresAt: string;
}

export function WalletTopUpDialog({
  open,
  onOpenChange,
  onSuccess,
}: WalletTopUpDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currencySymbol, formatCurrencyValue } = useOrgSettings();

  const [step, setStep] = useState<Step>("amount");
  const [topUpAmount, setTopUpAmount] = useState(DEFAULT_AMOUNT);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [qrData, setQrData] = useState<PayHubQRData | null>(null);
  const [countdown, setCountdown] = useState(300); // 5 minutes
  const [isPolling, setIsPolling] = useState(false);

  // Generate PayHub payment mutation
  const generatePaymentMutation = useMutation({
    mutationFn: async (amount: string) => {
      const res = await apiRequest("POST", "/api/customer/payhub/generate-qr", { 
        amount: parseFloat(amount)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to generate payment");
      }
      return res.json();
    },
    onSuccess: (data: PayHubQRData) => {
      setQrData(data);
      setIsPolling(true);
      const expiresAt = new Date(data.expiresAt).getTime();
      const remainingSeconds = Math.floor((expiresAt - Date.now()) / 1000);
      setCountdown(Math.max(0, remainingSeconds));
      setStep("qr");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to generate QR",
        description: error.message,
        variant: "destructive",
      });
      setStep("failed");
    },
  });

  // Poll for payment status
  useEffect(() => {
    if (!isPolling || !qrData?.paymentId || step !== "qr") return;

    const checkStatus = async () => {
      try {
        const res = await apiRequest("GET", `/api/customer/payhub/payment/${qrData.paymentId}`);
        if (res.ok) {
          const status = await res.json();
          if (status.status === "SUCCESS") {
            setIsPolling(false);
            setStep("success");
            queryClient.invalidateQueries({ queryKey: ["customer-wallet"] });
            queryClient.invalidateQueries({ queryKey: ["customer-recent-transactions"] });
          } else if (status.status === "FAILED" || status.status === "EXPIRED") {
            setIsPolling(false);
            setStep("failed");
          }
        }
      } catch (error) {
        console.error("Payment status check error:", error);
      }
    };

    const interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, [isPolling, qrData?.paymentId, step, queryClient]);

  // Countdown timer
  useEffect(() => {
    if (step === "qr" && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsPolling(false);
            setStep("failed");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, countdown]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep("amount");
        setTopUpAmount(DEFAULT_AMOUNT);
        setCustomAmount("");
        setIsCustomMode(false);
        setQrData(null);
        setCountdown(300);
        setIsPolling(false);
      }, 300);
    }
  }, [open]);

  const handleAmountSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = topUpAmount || customAmount;
    if (!amount || parseFloat(amount) < 100) {
      toast({
        title: "Invalid amount",
        description: `Minimum top-up amount is ${currencySymbol}100`,
        variant: "destructive",
      });
      return;
    }
    generatePaymentMutation.mutate(amount);
  };

  const handleClose = () => {
    setIsPolling(false);
    if (step === "success" && onSuccess) {
      onSuccess();
    }
    onOpenChange(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "gap-0 overflow-hidden p-0",
        step === "qr" ? "sm:max-w-2xl" : "sm:max-w-md"
      )}>
        {/* Amount Selection Step */}
        {step === "amount" && (
          <div className="relative">
            <div className="px-6 pt-6 pb-8 bg-gradient-to-br from-primary/5 via-primary/10 to-transparent">
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl font-semibold">Add Money</DialogTitle>
                    <DialogDescription>Quick & secure UPI payment</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="mt-6 text-center">
                <div className="inline-flex items-baseline gap-1">
                  <span className="text-2xl font-medium text-muted-foreground">{currencySymbol}</span>
                  <span className="text-5xl font-bold text-foreground">
                    {topUpAmount || customAmount || "0"}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleAmountSubmit} className="px-6 pb-6 space-y-5">
              <div className="flex flex-wrap justify-center gap-2">
                {QUICK_AMOUNTS.map((amt) => {
                  const isSelected = !isCustomMode && topUpAmount === amt.value;
                  return (
                    <button
                      key={amt.value}
                      type="button"
                      onClick={() => {
                        setTopUpAmount(amt.value);
                        setCustomAmount("");
                        setIsCustomMode(false);
                      }}
                      className={cn(
                        "relative px-5 py-2.5 rounded-full border-2 transition-all font-semibold text-sm",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-muted-foreground hover:border-primary/50"
                      )}
                    >
                      {currencySymbol}{amt.label}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                <Label htmlFor="customAmount">Or enter custom amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currencySymbol}</span>
                  <Input
                    id="customAmount"
                    type="number"
                    min="100"
                    step="1"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setTopUpAmount(e.target.value);
                      setIsCustomMode(true);
                    }}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Minimum: {currencySymbol}100</p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={generatePaymentMutation.isPending || (!topUpAmount && !customAmount)}
              >
                {generatePaymentMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating QR Code...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </form>
          </div>
        )}

        {/* QR Code Step */}
        {step === "qr" && qrData && (
          <div>
            <div className="px-6 pt-4 pb-2 border-b flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsPolling(false);
                  setStep("amount");
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div className="text-sm font-medium">
                Time left: {formatTime(countdown)}
              </div>
            </div>

            <div className="p-6 text-center">
              <DialogTitle className="text-2xl font-bold mb-2">
                Scan to Pay {currencySymbol}{topUpAmount || customAmount}
              </DialogTitle>
              <DialogDescription className="mb-6">
                Use any UPI app to scan and complete payment
              </DialogDescription>

              {/* QR Code */}
              <div className="inline-block p-6 bg-white rounded-2xl shadow-lg">
                <QRCodeSVG
                  value={qrData.paymentUrl}
                  size={256}
                  level="H"
                  includeMargin
                />
              </div>

              <div className="mt-6 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Scan with any UPI app like GPay, PhonePe, or Paytm
                </p>

                <div className="flex items-center justify-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                  <p className="text-sm font-medium text-primary">Waiting for payment...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === "success" && (
          <div className="p-8 text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold mb-2">
                Payment Successful!
              </DialogTitle>
              <DialogDescription>
                {currencySymbol}{topUpAmount || customAmount} has been added to your wallet
              </DialogDescription>
            </div>
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}

        {/* Failed Step */}
        {step === "failed" && (
          <div className="p-8 text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center">
              <span className="text-3xl">✕</span>
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold mb-2">
                Payment Failed
              </DialogTitle>
              <DialogDescription>
                {countdown <= 0 ? "QR code expired" : "Payment was not completed"}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Close
              </Button>
              <Button onClick={() => setStep("amount")} className="flex-1">
                Try Again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
